import React, { useState, useEffect } from 'react';
import { X, User, Calendar, Users as UsersIcon, Heart, Mail, Clock, PartyPopper, MessageCircle, Loader2, AlertTriangle, Ban } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useAuthStore } from '../../store/authStore';
import { userService, type UserGroup } from '../../services/userService';
import { MessageService } from '../../services/messageService';
import { useUserManagementStore } from '../../store/userManagementStore';
import ProfileAvatar from '../ui/ProfileAvatar';
import LoadingSpinner from '../ui/LoadingSpinner';
import ReportUserModal from './ReportUserModal';
import BlockConfirmationModal from './BlockConfirmationModal';

interface FullProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: number;
}


const FullProfileModal: React.FC<FullProfileModalProps> = ({
  isOpen,
  onClose,
  userId,
}) => {
  const { profile, isLoading, error, fetchProfile, clearProfile } = useUserProfile();
  const { user } = useAuthStore();
  const { getBlockStatus, unblockUser, clearErrors } = useUserManagementStore();
  const navigate = useNavigate();
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [canMessage, setCanMessage] = useState<boolean | null>(null);
  const [isCheckingMessage, setIsCheckingMessage] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [isCheckingBlock, setIsCheckingBlock] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);

  useEffect(() => {
    if (isOpen && userId) {
      fetchProfile(userId);
      fetchUserGroups(userId);
      checkCanMessage();
      checkBlockStatus();
    }
    
    return () => {
      if (!isOpen) {
        clearProfile();
        setUserGroups([]);
        setCanMessage(null);
        setIsBlocked(false);
        setShowReportModal(false);
        setShowBlockModal(false);
        clearErrors();
      }
    };
  }, [isOpen, userId, fetchProfile, clearProfile]);

  const fetchUserGroups = async (targetUserId: number) => {
    setGroupsLoading(true);
    try {
      const groups = await userService.getUserGroups(targetUserId);
      setUserGroups(groups);
    } catch (error) {
      console.error('Failed to fetch user groups:', error);
      setUserGroups([]);
    } finally {
      setGroupsLoading(false);
    }
  };

  // Check if users can message each other
  const checkCanMessage = async () => {
    if (!user || user.id === userId) {
      setCanMessage(false);
      return;
    }

    try {
      setIsCheckingMessage(true);
      const result = await MessageService.canMessageUser(userId.toString());
      setCanMessage(result.canMessage);
    } catch (error) {
      console.error('Failed to check message permission:', error);
      setCanMessage(false);
    } finally {
      setIsCheckingMessage(false);
    }
  };

  // Check block status
  const checkBlockStatus = async () => {
    if (!user || user.id === userId) {
      return;
    }

    try {
      setIsCheckingBlock(true);
      const blockStatus = await getBlockStatus(userId);
      setIsBlocked(blockStatus.currentUserBlocked);
    } catch (error) {
      console.error('Failed to check block status:', error);
      setIsBlocked(false);
    } finally {
      setIsCheckingBlock(false);
    }
  };

  // Handle message button click
  const handleMessageClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
    navigate(`/messages?user=${userId}`);
  };

  // Handle report button click
  const handleReportClick = () => {
    setShowReportModal(true);
  };

  // Handle block button click
  const handleBlockClick = () => {
    if (isBlocked) {
      handleUnblock();
    } else {
      setShowBlockModal(true);
    }
  };

  // Handle unblock action
  const handleUnblock = async () => {
    try {
      setIsActionLoading(true);
      await unblockUser(userId);
      setIsBlocked(false);
      // Recheck message permissions after unblocking
      checkCanMessage();
    } catch (error) {
      console.error('Failed to unblock user:', error);
    } finally {
      setIsActionLoading(false);
    }
  };

  // Handle successful block
  const handleBlockSuccess = () => {
    setIsBlocked(true);
    setCanMessage(false);
    setShowBlockModal(false);
  };

  const formatGender = (gender: string) => {
    const genderMap: { [key: string]: string } = {
      'MAN': 'Man',
      'VROUW': 'Vrouw',
      'TRANSGENDER': 'Transgender',
      'NON_BINAIR': 'Non-binair',
      'GENDERFLUIDE': 'Genderfluide',
      'ANDERS': 'Anders (leg uit in bio)',
      'LIEVER_NIET': 'Zeg ik liever niet',
      'man': 'Man',
      'vrouw': 'Vrouw',
      'transgender': 'Transgender',
      'non_binair': 'Non-binair',
      'genderfluide': 'Genderfluide',
      'anders': 'Anders (leg uit in bio)',
      'zeg_ik_liever_niet': 'Zeg ik liever niet',
    };
    return genderMap[gender] || gender;
  };

  const formatSocialPreference = (preference: string) => {
    const prefMap: { [key: string]: string } = {
      'INTROVERT': 'Introvert',
      'VERLEGEN_NIET_INTROVERT': 'Verlegen maar niet introvert',
      'INTROVERT_NUCHTER': 'Alleen introvert als ik nuchter ben',
      'GEMIDDELD': 'Gemiddeld',
      'BEETJE_EXTROVERT': 'Een beetje extrovert',
      'SUPER_EXTROVERT': 'Echt super extrovert',
      'introvert': 'Introvert',
      'verlegen_niet_introvert': 'Verlegen maar niet introvert',
      'introvert_nuchter': 'Alleen introvert als ik nuchter ben',
      'gemiddeld': 'Gemiddeld',
      'beetje_extrovert': 'Een beetje extrovert',
      'super_extrovert': 'Echt super extrovert',
    };
    return prefMap[preference] || 'Niet gespecificeerd';
  };

  const formatDrugUsePreference = (preference: string) => {
    const prefMap: { [key: string]: string } = {
      'VOLLEDIG_NUCHTER_IEDEREEN': 'Volledig nuchter, liefst iedereen om mij heen ook',
      'VOLLEDIG_NUCHTER_ANDEREN': 'Volledig nuchter, anderen mogen doen wat ze willen',
      'ALCOHOL_MET_ANDEREN': 'Ik drink alcohol, liefst met anderen',
      'ALCOHOL_ANDEREN_VRIJ': 'Ik drink alcohol, anderen mogen doen wat ze willen',
      'GA_HARD_ANDEREN_VRIJ': 'Ik ga hard, anderen mogen doen wat ze willen',
      'GA_HARD_MET_ANDEREN': 'Ik ga hard, liefst met anderen',
      'FLEXIBEL_OMGEVING': 'Ik ben flexibel, pas mij aan aan mijn omgeving',
      'volledig_nuchter_iedereen': 'Volledig nuchter, liefst iedereen om mij heen ook',
      'volledig_nuchter_anderen': 'Volledig nuchter, anderen mogen doen wat ze willen',
      'alcohol_met_anderen': 'Ik drink alcohol, liefst met anderen',
      'alcohol_anderen_vrij': 'Ik drink alcohol, anderen mogen doen wat ze willen',
      'ga_hard_anderen_vrij': 'Ik ga hard, anderen mogen doen wat ze willen',
      'ga_hard_met_anderen': 'Ik ga hard, liefst met anderen',
      'flexibel_omgeving': 'Ik ben flexibel, pas mij aan aan mijn omgeving',
    };
    if (!preference) return 'Niet gespecificeerd';
    return prefMap[preference] || preference;
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-canvas/70 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-surface border border-white/[.08] rounded-card px-4 pt-5 pb-4 text-left overflow-hidden shadow-panel transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-fg-bright">
              Volledig profiel
            </h3>
            <button
              onClick={onClose}
              className="text-fg-muted hover:text-fg-bright transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="space-y-6">
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
                <span className="ml-3 text-sm text-fg-muted">Loading profile...</span>
              </div>
            )}

            {error && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <p className="text-sm text-neon-pink-soft mb-3">Profiel laden mislukt</p>
                  <button
                    onClick={() => fetchProfile(userId)}
                    className="sf-link text-sm"
                  >
                    Opnieuw proberen
                  </button>
                </div>
              </div>
            )}

            {profile && !isLoading && !error && (
              <>
                {/* Profile Header */}
                <div className="flex items-start space-x-4">
                  <ProfileAvatar user={profile} size="xl" className="shadow-avatar-glow" />
                  <div className="flex-1">
                    <h4 className="text-2xl font-bold text-fg-bright mb-1">
                      {profile.username}
                    </h4>
                    {profile.createdAt && (
                      <div className="flex items-center text-sm text-fg-muted mb-3">
                        <Clock className="h-4 w-4 mr-1 text-neon-cyan" />
                        <span>
                          Lid sinds {new Date(profile.createdAt).toLocaleDateString('nl-NL', {
                            year: 'numeric',
                            month: 'long',
                          })}
                        </span>
                      </div>
                    )}

                    {/* Report and Block Actions */}
                    {user && user.id !== userId && (
                      <div className="flex items-center gap-2 mb-3">
                        {/* Report Button */}
                        <button
                          onClick={handleReportClick}
                          className="flex items-center px-3 py-1.5 text-xs font-medium text-warning-500 bg-warning-500/[.08] hover:bg-warning-500/[.15] border border-warning-500/30 rounded-lg transition-colors"
                          title="Gebruiker rapporteren"
                        >
                          <AlertTriangle className="h-3 w-3 mr-1.5" />
                          <span>Rapporteer</span>
                        </button>

                        {/* Block/Unblock Button */}
                        {isCheckingBlock ? (
                          <div className="flex items-center px-3 py-1.5 text-xs text-fg-meta">
                            <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                            <span>...</span>
                          </div>
                        ) : (
                          <button
                            onClick={handleBlockClick}
                            disabled={isActionLoading}
                            className={`flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors ${
                              isBlocked
                                ? 'text-fg-card bg-white/[.05] hover:bg-white/10 border-white/10'
                                : 'text-neon-pink-soft bg-neon-pink/[.08] hover:bg-neon-pink/[.15] border-neon-pink/30'
                            } ${isActionLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title={isBlocked ? 'Gebruiker deblokkeren' : 'Gebruiker blokkeren'}
                          >
                            {isActionLoading ? (
                              <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                            ) : (
                              <Ban className="h-3 w-3 mr-1.5" />
                            )}
                            <span>{isBlocked ? 'Deblokkeer' : 'Blokkeer'}</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Profile Information */}
                <div className="border-t border-white/[.08] pt-6">
                  <h5 className="text-lg font-bold text-fg-bright mb-4">Profielinformatie</h5>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      {profile.age && (
                        <div className="flex items-center text-sm text-fg-body">
                          <Calendar className="h-4 w-4 mr-3 text-neon-cyan" />
                          <span>{profile.age} years old</span>
                        </div>
                      )}

                      {profile.gender && (
                        <div className="flex items-center text-sm text-fg-body">
                          <User className="h-4 w-4 mr-3 text-neon-cyan" />
                          <span>{formatGender(profile.gender)}</span>
                        </div>
                      )}

                      {profile.socialPreference && (
                        <div className="flex items-center text-sm text-fg-body">
                          <Heart className="h-4 w-4 mr-3 text-neon-pink" />
                          <span>{formatSocialPreference(profile.socialPreference)}</span>
                        </div>
                      )}
                    </div>

                    <div className="space-y-4">
                      {profile.email && (
                        <div className="flex items-center text-sm text-fg-body">
                          <Mail className="h-4 w-4 mr-3 text-neon-cyan" />
                          <span>{profile.email}</span>
                        </div>
                      )}

                      {profile.drugUsePreference && (
                        <div className="text-sm text-fg-body">
                          <div className="flex items-center mb-2">
                            <PartyPopper className="h-4 w-4 mr-1 text-neon-pink" />
                            <span className="font-semibold text-fg-soft">Hoe sta jij op een feestje?</span>
                          </div>
                          <div className="ml-5 text-xs text-fg-muted">
                            {formatDrugUsePreference(profile.drugUsePreference)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Bio */}
                {profile.bio && (
                  <div className="pt-4">
                    <div className="sf-label-mono mb-2">Bio</div>
                    <p className="text-sm text-fg-body leading-relaxed">
                      {profile.bio}
                    </p>
                  </div>
                )}

                {/* Groups */}
                <div className="border-t border-white/[.08] pt-6">
                  <h5 className="text-lg font-bold text-fg-bright mb-3">Groepen</h5>

                  {groupsLoading && (
                    <div className="flex items-center py-4">
                      <LoadingSpinner size="sm" />
                      <span className="ml-2 text-sm text-fg-muted">Loading groups...</span>
                    </div>
                  )}

                  {!groupsLoading && userGroups.length === 0 && (
                    <div className="text-center py-8">
                      <UsersIcon className="h-12 w-12 text-neon-cyan mx-auto mb-3" />
                      <p className="text-sm italic text-fg-meta">Nog geen groepen</p>
                    </div>
                  )}

                  {!groupsLoading && userGroups.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {userGroups.map((group) => (
                        <div
                          key={group.id}
                          className="sf-item p-3 hover:bg-white/[.07] transition-colors"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <h6 className="font-semibold text-sm text-fg-bright">{group.name}</h6>
                            <span className={group.role === 'owner' ? 'sf-badge-lime' : 'sf-badge-cyan'}>
                              {group.role === 'owner' ? 'Owner' : 'Member'}
                            </span>
                          </div>
                          {group.eventName && (
                            <p className="text-xs text-fg-meta mb-1">{group.eventName}</p>
                          )}
                          <div className="flex items-center text-xs text-fg-meta">
                            <UsersIcon className="h-3 w-3 mr-1 text-neon-cyan" />
                            <span>{group.memberCount} members</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="mt-8 flex justify-between items-center">
            {/* Message Button */}
            <div>
              {user && user.id !== userId && (
                <>
                  {isCheckingMessage ? (
                    <div className="flex items-center text-sm text-fg-muted">
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      <span>Checking...</span>
                    </div>
                  ) : canMessage ? (
                    <button
                      onClick={handleMessageClick}
                      className="sf-btn-primary px-4 py-2 text-sm"
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      <span>Stuur bericht</span>
                    </button>
                  ) : canMessage === false ? (
                    <div className="flex items-center text-sm text-fg-meta">
                      <MessageCircle className="h-4 w-4 mr-2 opacity-50" />
                      <span className="text-xs">Je kunt alleen berichten sturen naar gebruikers uit je groepen</span>
                    </div>
                  ) : null}
                </>
              )}
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="sf-btn-secondary px-4 py-2 text-sm"
            >
              Sluiten
            </button>
          </div>
        </div>
      </div>

      {/* Report User Modal */}
      {profile && (
        <ReportUserModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          userId={userId}
          username={profile.username}
        />
      )}

      {/* Block Confirmation Modal */}
      {profile && (
        <BlockConfirmationModal
          isOpen={showBlockModal}
          onClose={() => setShowBlockModal(false)}
          userId={userId}
          username={profile.username}
          onSuccess={handleBlockSuccess}
        />
      )}
    </div>
  );
};

export default FullProfileModal;